/**
 * Accès aux paramètres du modèle (content/params.json) et aux chiffres de la baseline.
 * Toute valeur manquante ou `null` provoque une erreur explicite : le moteur ne devine jamais un chiffre (CLAUDE.md règles 1-2).
 */
import type { Baseline, Figure, ParamSet } from "./types.js";

export class EngineDataError extends Error {
  override name = "EngineDataError";
}

/** Valeur numérique d'un paramètre ; erreur si absent ou non relevé (`value: null`). */
export function param(params: ParamSet, id: string): number {
  const p = params[id];
  if (p === undefined) throw new EngineDataError(`params : paramètre manquant « ${id} » (content/params.json)`);
  if (p.value === null) throw new EngineDataError(`params : paramètre « ${id} » non relevé (value null)`);
  return p.value;
}

/** Paramètre optionnel : `undefined` si absent, erreur si présent mais null. */
export function paramOpt(params: ParamSet, id: string): number | undefined {
  const p = params[id];
  if (p === undefined) return undefined;
  if (p.value === null) throw new EngineDataError(`params : paramètre « ${id} » non relevé (value null)`);
  return p.value;
}

/** Valeur d'un chiffre de la baseline ; erreur si absent ou null. */
export function figure(fig: Figure | undefined, where: string): number {
  if (fig === undefined) throw new EngineDataError(`baseline : chiffre manquant « ${where} »`);
  if (fig.value === null) throw new EngineDataError(`baseline : chiffre « ${where} » non relevé (value null) — baseline incomplète`);
  return fig.value;
}

/** Chiffre d'un bloc `Record<string, Figure>` de la baseline. */
export function block(rec: Record<string, Figure>, key: string, where: string): number {
  return figure(rec[key], `${where}.${key}`);
}

/** Vérifie qu'une baseline est complète (aucun `null`, blocs obligatoires présents). */
export function assertBaselineComplete(b: Baseline): void {
  const hasMissions = b.state.missions.length > 0 || (b.state.missions_v03?.length ?? 0) > 0 || (b.state.lolf_missions?.length ?? 0) > 0;
  if (!hasMissions) throw new EngineDataError("baseline : aucune mission LOLF");
  if (b.state.revenues.length === 0 && (b.state.revenues_v03?.length ?? 0) === 0) throw new EngineDataError("baseline : aucune ligne de recettes");
  const walk = (node: unknown, path: string): void => {
    if (Array.isArray(node)) {
      node.forEach((x, i) => walk(x, `${path}[${i}]`));
    } else if (node && typeof node === "object") {
      const o = node as Record<string, unknown>;
      if ("value" in o && "unit" in o && o["value"] === null) {
        throw new EngineDataError(`baseline : chiffre « ${path} » non relevé (value null) — baseline incomplète`);
      }
      // `outlook` : trajectoire de référence à trous autorisés (valeur null = dernière valeur connue, ADR 0009 §6).
      for (const [k, v] of Object.entries(o)) if (k !== "meta" && !(path === "baseline" && k === "outlook")) walk(v, `${path}.${k}`);
    }
  };
  walk(b, "baseline");
}
